import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react'
import { getDrawerState, setDrawerOpen } from '../components/AgentChat/drawerState'

interface AgentChatContextType {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  onToggle: () => void
}

const AgentChatContext = createContext<AgentChatContextType | undefined>(undefined)

export const useAgentChat = (): AgentChatContextType => {
  const context = useContext(AgentChatContext)
  if (!context) {
    throw new Error('useAgentChat must be used within AgentChatProvider')
  }
  return context
}

interface AgentChatProviderProps {
  children: ReactNode
}

export const AgentChatProvider: React.FC<AgentChatProviderProps> = ({ children }) => {
  // Initialize from localStorage using consolidated state
  const [isOpen, setIsOpen] = useState<boolean>(() => getDrawerState().isOpen)

  const onOpen = useCallback(() => {
    setIsOpen(true)
    setDrawerOpen(true)
  }, [])

  const onClose = useCallback(() => {
    setIsOpen(false)
    setDrawerOpen(false)
  }, [])

  const onToggle = useCallback(() => {
    setIsOpen((prev) => {
      const newValue = !prev
      setDrawerOpen(newValue)
      return newValue
    })
  }, [])

  const value: AgentChatContextType = {
    isOpen,
    onOpen,
    onClose,
    onToggle,
  }

  return <AgentChatContext.Provider value={value}>{children}</AgentChatContext.Provider>
}

export { AgentChatContext }
